import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
	CreditCard,
	Globe,
	Smartphone,
	Plug,
	ShieldCheck,
	Rocket,
	CheckCircle2,
	ArrowRight,
} from "lucide-react";
import { MessageCircle } from "lucide-react";


export default function Servicios() {
	const baseUrl = "https://feapi.com.ar"; // ajustalo si corresponde
	const pageUrl = `${baseUrl}/feapi/servicios`;

	const services = [
		{
			title: "Chatbots & Automatización",
			desc: "Diseño e implementación de chatbots inteligentes para atención al cliente, ventas y automatización de procesos.",
			icon: MessageCircle,
			bullets: [
				"WhatsApp / Web / Instagram",
				"Respuestas automáticas + flujos",
				"Integración con APIs y sistemas",
			],
			tag: "Automatización",
		},

		{
			title: "Desarrollo de POS",
			desc: "Sistema de punto de venta rápido, estable y escalable: caja, productos, stock, clientes y reportes.",
			icon: CreditCard,
			bullets: [
				"Ventas + devoluciones",
				"Stock y precios",
				"Reportes y cierres",
			],
			tag: "Retail / Comercios",
		},
		{
			title: "Desarrollo Web",
			desc: "Sitios y dashboards modernos con foco en performance, SEO, seguridad y experiencia de usuario.",
			icon: Globe,
			bullets: ["Landing + SEO", "Panel admin", "Integraciones API"],
			tag: "Web Apps",
		},
		{
			title: "Desarrollo Mobile",
			desc: "Apps móviles (Android/iOS) para ventas, pedidos, tracking, turnos o atención al cliente.",
			icon: Smartphone,
			bullets: ["React Native", "Push notifications", "Auth + perfiles"],
			tag: "iOS / Android",
		},
		{
			title: "Integraciones & APIs",
			desc: "Conecto tu sistema con proveedores: facturación, pagos, WhatsApp, logística, CRM, ERPs.",
			icon: Plug,
			bullets: ["REST / Webhooks", "Jobs + colas", "Observabilidad"],
			tag: "Backend",
		},
		{
			title: "Infra & Deploy",
			desc: "Automatizo despliegues y entornos para que tu producto sea fácil de mantener y escalar.",
			icon: Rocket,
			bullets: ["CI/CD", "Docker", "Ambientes (dev/stage/prod)"],
			tag: "DevOps",
		},
	/*	{
			title: "Seguridad & Buenas prácticas",
			desc: "Hardening, control de accesos, validaciones, auditoría y estándares para evitar sorpresas.",
			icon: ShieldCheck,
			bullets: ["JWT/OAuth", "Roles/Permisos", "Logs + auditoría"],
			tag: "Calidad",
		},*/
	];

	const jsonLd = {
		"@context": "https://schema.org",
		"@type": "LocalBusiness",
		name: "FEAPI",
		url: baseUrl,
		areaServed: "AR",
		makesOffer: {
			"@type": "ItemList",
			itemListElement: services.map((s, i) => ({
				"@type": "ListItem",
				position: i + 1,
				item: {
					"@type": "Service",
					name: s.title,
					description: s.desc,
					provider: { "@type": "Organization", name: "FEAPI" },
				},
			})),
		},
	};

	return (
		<div className="min-h-screen bg-gradient-to-b from-slate-950 via-blue-950 to-slate-950">
			<Helmet>
				<html lang="es-AR" />
				<title>
					Servicios | FEAPI - Desarrollo POS, Web, Mobile e Integraciones
				</title>
				<meta
					name="description"
					content="Servicios de FEAPI: desarrollo de POS, aplicaciones web, mobile, integraciones API, infraestructura y seguridad. Soluciones profesionales listas para producción."
				/>
				<link rel="canonical" href={pageUrl} />

				{/* Open Graph */}
				<meta property="og:type" content="website" />
				<meta property="og:title" content="Servicios | FEAPI" />
				<meta
					property="og:description"
					content="Desarrollo POS, Web, Mobile, Integraciones y Deploy. Soluciones profesionales listas para producción."
				/>
				<meta property="og:url" content={pageUrl} />

				{/* Twitter */}
				<meta name="twitter:card" content="summary_large_image" />

				{/* JSON-LD */}
				<script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
			</Helmet>

			{/* Glow */}
			<div className="pointer-events-none absolute inset-0 overflow-hidden">
				<div className="absolute -top-24 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-blue-500/20 blur-3xl" />
				<div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-red-500/10 blur-3xl" />
			</div>

			<div className="relative mx-auto max-w-6xl px-6 py-12">
				{/* Breadcrumb / top actions */}
				<div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
					<div className="text-sm text-white/60">
						<Link to="/" className="hover:text-white">
							Inicio
						</Link>{" "}
						<span className="mx-2">/</span>
						<span className="text-white/80">Servicios</span>
					</div>

					<div className="flex gap-2">
						<a
							href="mailto:info@feapi.com.ar?subject=Quiero%20cotizar%20un%20servicio%20FEAPI"
							className="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-white/90"
						>
							Solicitar propuesta <ArrowRight className="h-4 w-4" />
						</a>
					</div>
				</div>

				{/* Hero */}
				<header className="rounded-3xl border border-white/10 bg-white/5 p-8 shadow-xl backdrop-blur">
					<p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white/80">
						<CheckCircle2 className="h-4 w-4" />
						Servicios profesionales FEAPI
					</p>

					<h1 className="mt-4 text-3xl font-bold tracking-tight text-white sm:text-4xl">
						Desarrollo de POS, Web y Mobile listo para producción
					</h1>

					<p className="mt-3 max-w-3xl text-white/70">
						Construimos soluciones modernas, mantenibles y escalables. Desde un
						POS para comercios hasta aplicaciones web y móviles, integraciones
						con APIs y despliegues sólidos.
					</p>

					<div className="mt-6 flex flex-col gap-3 sm:flex-row">
						<a
							href="mailto:info@feapi.com.ar?subject=Quiero%20una%20demo%20o%20reuni%C3%B3n%20FEAPI"
							className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white hover:bg-emerald-700"
						>
							Agendar demo <ArrowRight className="h-4 w-4" />
						</a>
						<a
							href="mailto:info@feapi.com.ar?subject=Consulta%20t%C3%A9cnica%20FEAPI"
							className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 px-5 py-3 text-sm font-semibold text-white hover:bg-white/10"
						>
							Hablar con un técnico <ArrowRight className="h-4 w-4" />
						</a>
					</div>
				</header>

				{/* Cards */}
				<section className="mt-10">
					<h2 className="text-xl font-bold text-white">Servicios</h2>
					<p className="mt-1 text-sm text-white/65">
						Elegí el tipo de desarrollo que necesitás. Podemos tomar un proyecto
						desde cero o mejorar uno existente.
					</p>

					<div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
						{services.map((s) => {
							const Icon = s.icon;
							return (
								<article
									key={s.title}
									className="group rounded-2xl border border-white/10 bg-white/5 p-6 shadow-sm backdrop-blur transition hover:bg-white/10"
								>
									<div className="flex items-start justify-between gap-3">
										<div className="flex items-center gap-3">
											<div className="rounded-2xl bg-white/10 p-3 ring-1 ring-white/10">
												<Icon className="h-5 w-5 text-white/85" />
											</div>
											<div>
												<h3 className="text-base font-semibold text-white">
													{s.title}
												</h3>
												<p className="mt-1 text-xs text-white/60">{s.tag}</p>
											</div>
										</div>

										<span className="rounded-full bg-white/10 px-3 py-1 text-[11px] font-semibold text-white/70">
											FEAPI
										</span>
									</div>

									<p className="mt-4 text-sm text-white/70">{s.desc}</p>

									<ul className="mt-4 space-y-2 text-sm text-white/70">
										{s.bullets.map((b) => (
											<li key={b} className="flex items-center gap-2">
												<CheckCircle2 className="h-4 w-4 text-emerald-300" />
												<span>{b}</span>
											</li>
										))}
									</ul>

									<div className="mt-6">
										<a
											href={`mailto:info@feapi.com.ar?subject=${encodeURIComponent(
												"Consulta servicio FEAPI: " + s.title,
											)}`}
											className="inline-flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-white"
										>
											Pedir info{" "}
											<ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
										</a>
									</div>
								</article>
							);
						})}
					</div>
				</section>

				{/* Why us */}
				<section className="mt-10 grid grid-cols-1 gap-4 lg:grid-cols-3">
					<div className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur">
						<h3 className="text-sm font-semibold text-white">
							Entrega profesional
						</h3>
						<p className="mt-2 text-sm text-white/70">
							Código limpio, documentación, y una base sólida para que el
							producto crezca sin romperse.
						</p>
					</div>
					<div className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur">
						<h3 className="text-sm font-semibold text-white">
							Foco en negocio
						</h3>
						<p className="mt-2 text-sm text-white/70">
							Priorizamos impacto: ventas, eficiencia operativa, automatización
							y reducción de errores.
						</p>
					</div>
					<div className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur">
						<h3 className="text-sm font-semibold text-white">Escalabilidad</h3>
						<p className="mt-2 text-sm text-white/70">
							Arquitectura preparada para más usuarios, más módulos e
							integraciones.
						</p>
					</div>
				</section>

				{/* CTA bottom */}
				<section className="mt-10 rounded-3xl border border-white/10 bg-white/5 p-8 text-center shadow-xl backdrop-blur">
					<h2 className="text-2xl font-bold text-white">¿Empezamos?</h2>
					<p className="mx-auto mt-2 max-w-2xl text-sm text-white/70">
						Contanos qué necesitás (POS, web, mobile o integración) y te
						respondemos con una propuesta clara.
					</p>
					<div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
						<a
							href="mailto:info@feapi.com.ar?subject=Quiero%20cotizar%20un%20servicio%20FEAPI&body=Hola%2C%20quisiera%20cotizar%20un%20servicio.%0A%0A-%20Servicio%3A%20%0A-%20Detalle%3A%20%0A-%20Plazo%20ideal%3A%20%0A"
							className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-semibold text-slate-900 hover:bg-white/90"
						>
							Solicitar cotización <ArrowRight className="h-4 w-4" />
						</a>
						<Link
							to="/login"
							className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 px-5 py-3 text-sm font-semibold text-white hover:bg-white/10"
						>
							Entrar al panel <ArrowRight className="h-4 w-4" />
						</Link>
					</div>
				</section>

				<footer className="mt-10 text-center text-xs text-white/45">
					© {new Date().getFullYear()} FEAPI
				</footer>
			</div>
		</div>
	);
}
